/**
 * Core-100 R5 (CORE-AB S) — the ranking audit is server-authored.
 *
 * `assignment_audit` is what the Ranking tab and the Command Center score viewer present as THE ranking of a ticket.
 * Browsers no longer write it (the INSERT policy is gone and the grant is revoked). smart-assign records every ranking
 * it is asked to record and returns `data.rankingRecord`; a caller that then creates the ticket attaches that run to
 * it with `record_assignment_selection` — supplying only the ticket and the ranked technician it chose, never a score.
 *
 * Two pure pieces live here so every consumer reads the table the same way:
 *  - `readRankingRecord`      the run smart-assign recorded, from its response envelope;
 *  - `selectCurrentRanking`   which rows of a ticket are its current ranking (one run, the attached copy preferred).
 * and one effect, `recordAssignmentSelection`, which reports a failure instead of dropping it.
 */
import { supabase } from '@/integrations/supabase/client';

export interface RankingRecord {
  runId: string | null;
  ticketId: string | null;
  recorded: boolean;
  error?: string;
}

/** smart-assign's `data.rankingRecord` (canonical envelope), or null when the response carried none. */
export function readRankingRecord(response: unknown): RankingRecord | null {
  const env = (response as { data?: { rankingRecord?: unknown } } | null)?.data;
  const rec = env?.rankingRecord as RankingRecord | undefined;
  if (!rec || typeof rec !== 'object' || typeof rec.recorded !== 'boolean') return null;
  return rec;
}

export interface RankingAuditRow {
  tech_id: string;
  created_at: string;
  selected?: boolean | null;
  ranking_run_id?: string | null;
  source?: string | null;
}

/**
 * The rows that ARE the ticket's ranking. Rows written before R5 carry no run and are returned as they are. Otherwise
 * the most recent run wins, and within it the copy attached with the choice made (`smart-assign:selection`) is
 * preferred over the server's original of the same run — the two are the same ranking, never both shown.
 */
export function selectCurrentRanking<T extends RankingAuditRow>(rows: readonly T[]): T[] {
  const withRun = rows.filter((r) => !!r.ranking_run_id);
  if (withRun.length === 0) return [...rows];
  const latest = withRun.reduce((a, b) => (Date.parse(b.created_at) > Date.parse(a.created_at) ? b : a));
  const run = withRun.filter((r) => r.ranking_run_id === latest.ranking_run_id);
  const attached = run.filter((r) => r.source === 'smart-assign:selection');
  return attached.length > 0 ? attached : run;
}

/**
 * Attach the server's ranking run to the ticket just created (or placed) and record the technician chosen.
 * Returns true when the database recorded it (or had already recorded this same selection). A missing run or a
 * refusal is reported to the client error buffer — the ranking of that ticket is then visibly absent, not invented.
 */
export async function recordAssignmentSelection(args: {
  rankingRecord: RankingRecord | null;
  ticketId: string;
  selectedTechId: string | null;
  shopId: string;
  source: string;
}): Promise<boolean> {
  const { rankingRecord, ticketId, selectedTechId, shopId, source } = args;
  const report = (message: string, extra: Record<string, unknown>) => {
    console.error(`[AUDIT] ${message}`, extra);
    void import('@/utils/clientErrorBuffer').then(({ pushClientError }) => {
      pushClientError({ source: 'mutation', message: `[AUDIT] ${message}`, extra: { ticketId, ...extra, caller: source }, shopId });
    });
  };
  if (!rankingRecord?.recorded || !rankingRecord.runId) {
    report('smart-assign did not record this ranking; the ticket has no ranking audit', {
      rankingError: rankingRecord?.error ?? 'no ranking record returned',
    });
    return false;
  }
  const { error } = await supabase.rpc('record_assignment_selection' as never, {
    p_ranking_run_id: rankingRecord.runId,
    p_ticket_id: ticketId,
    p_selected_tech_id: selectedTechId,
  } as never);
  if (error) {
    report('the ranking selection was not recorded', { runId: rankingRecord.runId, error: error.message });
    return false;
  }
  return true;
}
